import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { AuthorizationSelectors } from '@next/core-lib/authorization';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { LocaleService } from '@next/core-lib/i18n';
import { RouterSelectors } from '@next/core-lib/router-store';
import { SiteActions } from '@next/core-lib/site';
import { TableColumnTypes, TableConfig, TableFooterValueTypes } from '@next/core-lib/table';
import { UserSelectors } from '@next/core-lib/user';
import { WindowService, WindowServiceInjector } from '@next/core-lib/window';
import { select, Store } from '@ngrx/store';
import { combineLatest, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, map, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import {
  ActiveIngredient,
  CropFieldAdvice,
  PlanetProofCrop,
  PlanetProofReport,
  PlanetProofReportCropField,
  SiteState,
} from 'src/app/shared/models';
import { isPlanetProofCalculationComplete } from 'src/app/shared/util/planet-proof-report';
import { GetPlanetProofReport } from 'src/app/store/actions';
import {
  getCropYear,
  getReportSort,
  selectCropFieldAdvices,
  selectLoggedInEmployeeAsArray,
  selectPlanetProofReport,
} from 'src/app/store/selectors';
import { Environment } from 'src/environments/environment';

import { ReportSort, ReportSortOption } from '../models';
import { defaultReportTableConfig } from '../reportDefaults';
import { ReportSortService } from '../services/report-sort.service';

interface CropFieldOverviewDataSource {
  name: string;
  cropName: string;
  areaInHectares: number;
}

interface CropFieldTable {
  cropFieldId: number;
  hasDetails: boolean;
  title: string;
  cropName: string;
  cropNormTitle: string;
  cropNorm: number;
  hectares: number;
  totalActiveMatter: number | null;
  sumTotalBonusMalus: number | null;
  tableConfig: TableConfig;
}

@Component({
  selector: 'app-planet-proof-report',
  templateUrl: './planet-proof-report.component.html',
  styleUrls: ['./../reports.scss'],
  standalone: false,
})
export class PlanetProofReportComponent implements OnInit, OnDestroy {
  readonly usingArableReport = inject<Environment>(EnvironmentInjector)
    .USE_ARABLE_SERVICE_PLANET_PROOF_REPORT;
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly locale = inject(LocaleService);
  private readonly sortService = inject(ReportSortService);
  protected readonly router = inject(Router);
  private readonly window = inject<WindowService>(WindowServiceInjector);

  readonly CropYearCalculationChange = 2026;
  destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  planetProofReport$ = this.store.pipe(select(selectPlanetProofReport));
  planetProofReport: PlanetProofReport;
  cropYear$ = this.store.pipe(select(getCropYear));
  cropFieldIds$ = this.store.pipe(select(RouterSelectors.getRouteParam('cropFieldIds')));
  customer$ = this.store.pipe(select(UserSelectors.selectCustomer));
  relatedPeople$ = this.store.pipe(select(selectLoggedInEmployeeAsArray));
  cropFieldAdvices$ = this.store.pipe(select(selectCropFieldAdvices));
  cropFieldAdvices: CropFieldAdvice[] = [];
  pageTitle$ = combineLatest([this.cropYear$, this.customer$]).pipe(
    map(
      ([cropYear, customer]) =>
        `${customer?.name ? customer.name : ''} - ${this.locale.translate(
          'gbmPlanetProof',
        )} ${cropYear}`,
    ),
  );
  cropFieldOverviewPlanetProofTableConfig: TableConfig;
  cropFieldOverviewOtherTableConfig: TableConfig;
  cropTableConfig: TableConfig;
  cropFieldTables: CropFieldTable[] = [];

  reportSort$ = this.store.pipe(select(getReportSort));
  reportSort: ReportSort;

  buttonItems$ = this.sortService.getButtonItems();

  get calculationIncomplete(): boolean {
    return !!this.planetProofReport && !isPlanetProofCalculationComplete(
      this.planetProofReport, this.usingArableReport,
    );
  }

  get averageGlyphosateUsage(): number | null {
    const report = this.planetProofReport;
    return !this.calculationIncomplete && report?.totalHectares > 0
      ? report.totalGlyphosateUsage / report.totalHectares : null;
  }

  ngOnInit() {
    this.store
      .pipe(
        select(AuthorizationSelectors.hasAtLeastAdvisorRoleForApplicationKey('PlanetProof')),
        take(1),
      )
      .subscribe((hasCorrectRole) => {
        if (!hasCorrectRole) {
          this.router.navigate(['/'], { queryParamsHandling: 'preserve' });
        }
      });

    this.cropYear$
      .pipe(withLatestFrom(this.cropFieldIds$), distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe(([cropYear, fieldIdsString]) => {
        if (cropYear) {
          const fieldIds = JSON.parse(fieldIdsString) as number[];
          this.store.dispatch(new GetPlanetProofReport(cropYear, fieldIds));
        }
      });

    this.reportSort$.pipe(takeUntil(this.destroyed$)).subscribe((reportSort) => {
      this.reportSort = reportSort;
      if (this.cropFieldAdvices) {
        this.setCropFieldOverviewTables(this.cropFieldAdvices);
      }
      if (this.planetProofReport) {
        this.setCropTable(this.planetProofReport.crops);
        this.setCropFieldTables(this.planetProofReport.cropFields, this.planetProofReport.crops);
      }
    });

    this.store.dispatch(
      SiteActions.setPageTitle({ pageTitle: this.locale.translate('planetProofGBMReport') }),
    );

    this.cropFieldAdvices$.pipe(takeUntil(this.destroyed$)).subscribe((cropFieldAdvices) => {
      if (!cropFieldAdvices) {
        return;
      }
      this.cropFieldAdvices = cropFieldAdvices;
      this.setCropFieldOverviewTables(cropFieldAdvices);
    });

    this.planetProofReport$.pipe(takeUntil(this.destroyed$)).subscribe((planetProofReport) => {
      if (!planetProofReport) {
        return;
      }
      this.planetProofReport = planetProofReport;
      this.setCropTable(planetProofReport.crops);
      this.setCropFieldTables(planetProofReport.cropFields, planetProofReport.crops);
    });
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  setCropFieldOverviewTables(cropFieldAdvices: CropFieldAdvice[]) {
    const columns = [
      {
        id: 'name',
        label: this.locale.translate('name'),
        type: TableColumnTypes.STRING,
      },
      {
        id: 'cropName',
        label: this.locale.translate('crop'),
        type: TableColumnTypes.STRING,
      },
      {
        id: 'areaInHectares',
        label: this.locale.translate('hectare'),
        type: TableColumnTypes.NUMBER,
        footerValueType: TableFooterValueTypes.SUM,
        footerDescription: this.locale.translate('total'),
        digitsInfo: '1.2-2',
      },
    ];
    const planetProofTableConfig = {
      ...defaultReportTableConfig,
      footer: true,
      columns,
    };
    const otherTableConfig = {
      ...defaultReportTableConfig,
      footer: true,
      columns,
    };
    const dataSources: {
      planetProof: CropFieldOverviewDataSource[];
      other: CropFieldOverviewDataSource[];
    } = {
      planetProof: [],
      other: [],
    };

    const sortedAdvices = this.sortService.sort({
      elements: cropFieldAdvices,
      option: this.reportSort.option,
      descending: this.reportSort.descending,
      cropFieldNameFunc: (a) => a.name,
      cropNameFunc: (a) => a.crop?.name,
    });

    sortedAdvices.forEach((advice) => {
      const target = advice.isPlanetProof ? dataSources.planetProof : dataSources.other;
      target.push({
        name: advice.name,
        cropName: advice.crop?.name,
        areaInHectares: advice.areaInHectares,
      });
    });
    planetProofTableConfig.dataSource = new MatTableDataSource(dataSources.planetProof);
    otherTableConfig.dataSource = new MatTableDataSource(dataSources.other);
    this.cropFieldOverviewPlanetProofTableConfig = planetProofTableConfig;
    this.cropFieldOverviewOtherTableConfig = otherTableConfig;
  }

  setCropTable(crops: PlanetProofCrop[]) {
    const tableConfig = {
      ...defaultReportTableConfig,
      columns: [
        {
          id: 'cropName',
          label: this.locale.translate('cropName'),
          type: TableColumnTypes.STRING,
          className: 'crop-name-column',
        },
        {
          id: 'areaInHectares',
          label: this.locale.translate('hectare'),
          type: TableColumnTypes.NUMBER,
          digitsInfo: '1.2-2',
        },
        {
          id: 'activeMatter',
          label: this.locale.translate('kgActiveIngredientHa'),
          type: TableColumnTypes.NUMBER,
          digitsInfo: '1.2-2',
        },
        {
          id: 'cropNorm',
          label: this.locale.translate('normActiveIngredient'),
          type: TableColumnTypes.NUMBER,
          digitsInfo: '1.2-2',
        },
        {
          id: 'bonus',
          label: this.locale.translate('bonus'),
          type: TableColumnTypes.NUMBER,
        },
        {
          id: 'malus',
          label: this.locale.translate('malus'),
          type: TableColumnTypes.NUMBER,
        },
        {
          id: 'extraBonus',
          label: this.locale.translate('extraBonus'),
          type: TableColumnTypes.NUMBER,
        },
        {
          id: 'saldo',
          label: this.locale.translate('balance'),
          type: TableColumnTypes.NUMBER,
        },
      ],
    };
    const dataSource: {
      cropName: string;
      areaInHectares: number;
      activeMatter: number | null;
      cropNorm: number | null;
      bonus: number | null;
      malus: number | null;
      extraBonus: number | null;
      saldo: number | null;
    }[] = [];
    [...crops].forEach((crop) => {
      dataSource.push({
        cropName: crop.cropName,
        areaInHectares: crop.areaInHectares,
        activeMatter: this.calculationIncomplete ? null : crop.activeMatter,
        cropNorm: crop.cropNorm,
        bonus: this.calculationIncomplete ? null : crop.bonus,
        malus: this.calculationIncomplete ? null : crop.malus,
        extraBonus: this.calculationIncomplete ? null : crop.extraBonus,
        saldo: this.calculationIncomplete ? null : crop.saldo,
      });
    });
    const sortedDataSource = this.sortService.sort({
      elements: dataSource,
      option: this.reportSort.option,
      descending:
        this.reportSort.option === ReportSortOption.sortCropName
          ? this.reportSort.descending
          : false,
      cropFieldNameFunc: () => '',
      cropNameFunc: (item) => item.cropName,
    });
    tableConfig.dataSource = new MatTableDataSource(sortedDataSource);
    this.cropTableConfig = tableConfig;
  }

  setCropFieldTables(cropFields: PlanetProofReportCropField[], crops: PlanetProofCrop[]) {
    const cropFieldTables: CropFieldTable[] = [];

    const sortedCropFields = this.sortService.sort({
      elements: cropFields,
      option: this.reportSort.option,
      descending: this.reportSort.descending,
      cropFieldNameFunc: (f) => f.cropFieldName,
      cropNameFunc: (f) => f.cropName,
    });

    sortedCropFields.forEach((cropField) => {
      const tableConfig = {
        ...defaultReportTableConfig,
        // The table library reduces null values to zero in SUM footers.
        footer: !this.calculationIncomplete,
        columns: [
          {
            id: 'name',
            label: this.locale.translate('productAlt'),
            type: TableColumnTypes.STRING,
          },
          {
            id: 'admissionNumber',
            label: this.locale.translate('admissionNumber'),
            type: TableColumnTypes.STRING,
          },
          {
            id: 'type',
            label: this.locale.translate('typeProductAlt'),
            type: TableColumnTypes.STRING,
          },
          {
            id: 'dosage',
            label: this.locale.translate('dosageHa'),
            type: TableColumnTypes.STRING,
          },
          {
            id: 'activeIngredients',
            label: this.locale.translate('activeIngredients'),
            type: TableColumnTypes.STRING,
          },
          {
            id: 'kgAsHa',
            label: this.locale.translate('kgAsHa'),
            type: TableColumnTypes.NUMBER,
            footerValueType: TableFooterValueTypes.SUM,
            footerDescription: this.locale.translate('total'),
            digitsInfo: '1.3-6',
          },
          {
            id: 'bonus',
            label: this.locale.translate('bonus'),
            type: TableColumnTypes.NUMBER,
            footerValueType: TableFooterValueTypes.SUM,
            footerDescription: `${this.locale.translate('total')} ${this.locale.translate(
              'bonus',
            )}`,
          },
          {
            id: 'malus',
            label: this.locale.translate('malus'),
            type: TableColumnTypes.NUMBER,
            footerValueType: TableFooterValueTypes.SUM,
            footerDescription: `${this.locale.translate('total')} ${this.locale.translate(
              'malus',
            )}`,
          },
        ],
      };
      const dataSource: {
        name: string;
        admissionNumber: string;
        type: string;
        dosage: string;
        activeIngredients: string;
        kgAsHa: number | null;
        bonus: number | null;
        malus: number | null;
      }[] = [];
      const foundCropNorm = crops.find((crop) => crop.cropId === cropField?.cropId)?.cropNorm;
      const cropFieldTable: CropFieldTable = {
        cropFieldId: cropField.cropFieldId,
        hasDetails: cropField.adviceId !== 0 && Array.isArray(cropField.cropAids),
        title: `${cropField.cropFieldName}: ${cropField.areaInHectares} ${this.locale.translate(
          'ha',
        )}, ${cropField.cropName}`,
        cropName: cropField.cropName,
        cropNormTitle: foundCropNorm ? `${foundCropNorm} ${this.locale.translate('kgAsHa')}` : '',
        cropNorm: foundCropNorm ?? Infinity,
        hectares: cropField.areaInHectares,
        totalActiveMatter: this.calculationIncomplete ? null : cropField.totalActiveMatter ?? null,
        sumTotalBonusMalus: this.calculationIncomplete ? null : cropField.saldo ?? null,
        tableConfig,
      };
      cropField.cropAids?.forEach((cropAid) => {
        const ingredients = cropAid.activeIngredients ?? [];
        const countedAmounts = ingredients.map((i) => i.kgAsPerHA)
          .filter((amount): amount is number => amount !== null && amount !== undefined);
        dataSource.push({
          name: cropAid.name,
          admissionNumber: this.filterCropAidAdmissionNumber(ingredients),
          type: !cropAid.type ? '' : this.locale.translate(`cropProtectionType.${cropAid.type}`),
          dosage: `${cropAid.dosage} ${cropAid.unitOfMeasurement}`,
          activeIngredients: ingredients.map((i) => i.forbidden
            ? `${i.name} (${this.locale.translate('ingredientForbiddenInPlanetProof')})`
            : i.name).join(', '),
          kgAsHa: this.calculationIncomplete || !countedAmounts.length
            ? null : countedAmounts.reduce((a, b) => a + b, 0),
          bonus: this.calculationIncomplete ? null : cropAid.bonus,
          malus: this.calculationIncomplete || !ingredients.length
            ? null : ingredients.reduce((total, ingredient) => total + ingredient.malus, 0),
        });
      });
      tableConfig.dataSource = new MatTableDataSource(dataSource);
      cropFieldTables.push(cropFieldTable);
    });
    this.cropFieldTables = cropFieldTables;
  }

  filterCropAidAdmissionNumber(ingredients: ActiveIngredient[]): string {
    return ingredients
      .map((i) => i.admissionNumber)
      .filter((el, i, a) => i === a.indexOf(el))
      .join(', ');
  }

  print() {
    if (this.window) {
      this.window.print();
    }
  }

  back() {
    this.router.navigate(['/']);
  }

  buttonAction(data: string) {
    this.sortService.handleButtonAction(data);
  }
}
